/* A system PDP can start the existing finder without touching cart or subscription inputs.
 * Compatibility stays in cq-wiz-refills.json; variant resolution belongs to the finder. */
(function () {
  'use strict';
  if (customElements.get('cq-system-replacements-link')) return;
  const catalogues = new Map();
  const variantId = value => /^[1-9]\d*$/.test(String(value || '')) && Number.isSafeInteger(Number(value)) ? String(value) : '';
  const json = element => { try { return JSON.parse(element ? element.textContent : '{}'); } catch (_) { return {}; } };
  function catalogue(url) {
    if (!catalogues.has(url)) {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
      const request = fetch(url, {credentials:'same-origin', signal:controller.signal})
        .then(response => { if (!response.ok) throw new Error('Catalogue unavailable'); return response.json(); })
        .then(data => { if (data.version !== 1 || !Array.isArray(data.systems)) throw new Error('Catalogue unavailable'); return data; })
        .finally(() => clearTimeout(timer));
      // Keep the promise across section reconnects, including failures: one request per catalogue per page.
      catalogues.set(url, request);
    }
    return catalogues.get(url);
  }
  customElements.define('cq-system-replacements-link', class extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.link = this.querySelector('a');
      this.purchase = this.closest('cq-purchase-options');
      if (!this.link || !this.purchase || !this.dataset.catalogUrl || this.dataset.commercial === 'true') return;
      this.scope = this.closest('.shopify-section') || this.closest('.section-content') || this.purchase.parentElement;
      const form = this.closest('form'), selected = form && form.querySelector('[name="id"]');
      this.selectedVariant = variantId(selected ? selected.value : this.dataset.variantId);
      this.onVariant = event => {
        if (event.target !== this.purchase) return;
        this.selectedVariant = variantId(event.detail && event.detail.variant && event.detail.variant.id);
        this.updateLink();
        // Parts cards listen later, at document level. Recheck after their synchronous variant update finishes.
        this.schedulePresentation();
      };
      this.purchase.addEventListener('cq:variant-change', this.onVariant);
      this.onParts = event => { if (event.target.matches('cq-related-parts')) this.schedulePresentation(); };
      this.scope.addEventListener('cq:rel-change', this.onParts);
      const connection = this.connection = {};
      catalogue(this.dataset.catalogUrl).then(data => {
        if (!this.isConnected || this.connection !== connection) return;
        const handle = this.dataset.systemHandle;
        const system = data.systems.find(system => system.handle === handle) || (data.callUsSystems || []).find(system => system.handle === handle);
        this.supported = !!handle && (system ? system.group !== 'commercial' :
          Object.values(data.notices || {}).some(notice => notice.link && notice.link.handle === handle));
        this.updateLink();
      }).catch(() => { if (this.connection === connection) this.hidden = true; });
    }
    disconnectedCallback() {
      if (this.purchase && this.onVariant) this.purchase.removeEventListener('cq:variant-change', this.onVariant);
      if (this.scope && this.onParts) this.scope.removeEventListener('cq:rel-change', this.onParts);
      this.removeHelper();
      this.connection = null;
      this.supported = false;
    }
    updateLink() {
      if (!this.supported || !this.link) return;
      const url = new URL(this.link.href, location.href);
      url.searchParams.set('path', 'replace');
      url.searchParams.set('start', '1');
      url.searchParams.set('system', this.dataset.systemHandle);
      if (this.selectedVariant) url.searchParams.set('variant', this.selectedVariant);
      else url.searchParams.delete('variant');
      this.link.setAttribute('href', url.pathname + url.search + url.hash);
      this.finderURL = this.link.getAttribute('href');
      this.syncPresentation();
    }
    schedulePresentation() {
      const connection = this.connection;
      Promise.resolve().then(() => { if (connection && this.connection === connection) this.syncPresentation(); });
    }
    currentOffers() {
      const offer = this.purchase.querySelector('[data-cq-replace]');
      if (!offer || offer.hidden) return null;
      const matched = Array.from(offer.querySelectorAll('[data-cq-replace-card]')).some(card => {
        if (card.hidden) return false;
        const data = json(card.querySelector('[data-cq-replace-data]')), key = data.map && data.map[this.selectedVariant];
        const item = key && data.items && data.items[String(key).split('@')[0]];
        const input = card.querySelector('[data-cq-replace-input]');
        return item && variantId(item.id) && input && input.dataset.variantId === String(item.id);
      });
      return matched ? offer : null;
    }
    currentParts() {
      for (const parts of this.scope.querySelectorAll('cq-related-parts')) {
        const group = parts.querySelector('[data-cq-rel-parts]'), map = json(parts.querySelector('[data-cq-rel-map]')).v;
        const entry = map && map[this.selectedVariant];
        if (!group || group.hidden || !entry || !Array.isArray(entry.p)) continue;
        const ids = new Set(entry.p.map(row => Array.isArray(row) && variantId(row[0])).filter(Boolean));
        const card = Array.from(group.querySelectorAll('[data-cq-part]')).find(card =>
          !card.hidden && ids.has(card.dataset.cqPart) && card.querySelector('.cq-rel-part-name[href]'));
        if (!card) continue;
        // An inactive tab is an existing shopping destination, not a reason to create another owner panel.
        let blocked = false;
        for (let node = group; node && node !== this.scope; node = node.parentElement) {
          if (node.hidden && !node.matches('.tabcontent')) { blocked = true; break; }
        }
        if (!blocked) return group;
      }
      return null;
    }
    syncPresentation() {
      if (!this.supported || !this.isConnected) return;
      const offer = this.currentOffers(), parts = this.currentParts();
      this.hidden = !!(offer || parts);
      // AJ 2026-10-21: whole-house RO offers show one complete pack and no extra link before Add to cart.
      if (!offer || offer.matches('[data-cq-ro-replacements]')) { this.removeHelper(); return; }
      if (!this.helper) {
        // A section re-render or duplicate render must never create two standalone-shopping links.
        if (this.purchase.querySelector('[data-cq-replacements-only]')) return;
        this.helper = document.createElement('a');
        this.helper.className = 'cq-system-replacements__helper';
        this.helper.setAttribute('data-cq-replacements-only', '');
        this.helper.textContent = this.dataset.shopOnly;
        this.helper.addEventListener('click', event => {
          if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const target = this.currentParts();
          if (target && this.openParts(target)) event.preventDefault();
        });
      }
      // A real finder URL also works for modified clicks/new tabs. Normal clicks open existing mapped Parts cards.
      this.helper.href = this.finderURL;
      if (this.helper.parentElement !== offer) offer.append(this.helper);
    }
    openParts(group) {
      const panel = group.closest('.tabcontent'), expandable = group.closest('collapsible-expandable');
      if (panel && panel.hidden) {
        const button = Array.from(this.scope.querySelectorAll('.tablinks')).find(button => button.dataset.tab === panel.id);
        if (!button) return false;
        button.click();
        if (panel.hidden) return false;
      }
      if (expandable) {
        if (typeof expandable.expand !== 'function') return false;
        expandable.expand();
      }
      const target = group.querySelector('.cq-rel-title') || group;
      target.style.scrollMarginTop = 'var(--cq-pdp-anchor-offset, 160px)';
      target.setAttribute('tabindex', '-1');
      target.focus({preventScroll:true});
      target.scrollIntoView({block:'start', behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      return true;
    }
    removeHelper() {
      if (this.helper) this.helper.remove();
      this.helper = null;
    }
  });
})();
